import { useState, useMemo, useRef } from 'react';
import type { Lavoro, Preventivo, EstimateLevel, TipoAcquisto } from '../../types';
import {
  formatCurrency,
  calculateCmeTotals,
  calculateEscluse,
  getImportoStimato,
  getDelta,
  hasUsatoNuovo,
  isAttiva,
} from '../../utils/pricing';
import { useUpdatePreventivo, useDeletePreventivo } from '../../hooks';

interface CmeViewProps {
  lavoro: Lavoro;
  estimateLevel: EstimateLevel;
  projectId: string;
  onClose: () => void;
}

export function CmeView({ lavoro, projectId, onClose }: CmeViewProps) {
  const preventivi = useMemo(() => lavoro.preventivi || [], [lavoro.preventivi]);
  const updatePreventivo = useUpdatePreventivo();
  const deletePreventivo = useDeletePreventivo();
  const printRef = useRef<HTMLDivElement>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ voce: '', note: '', importoStimato: '' });
  const [copied, setCopied] = useState(false);

  // Group by category (preserve insertion order via ordine)
  const grouped = useMemo(() => {
    const map = new Map<string, Preventivo[]>();
    const sorted = [...preventivi].sort((a, b) => a.ordine - b.ordine);
    for (const p of sorted) {
      const list = map.get(p.categoria) || [];
      list.push(p);
      map.set(p.categoria, list);
    }
    return Array.from(map.entries());
  }, [preventivi]);

  const totals = useMemo(() => calculateCmeTotals(preventivi), [preventivi]);
  const escluse = useMemo(() => calculateEscluse(preventivi), [preventivi]);
  const hasAnyDouble = useMemo(() => preventivi.some(hasUsatoNuovo), [preventivi]);
  const allAttive = useMemo(() => preventivi.every(isAttiva), [preventivi]);
  const noneAttive = useMemo(() => preventivi.every((p) => !isAttiva(p)), [preventivi]);

  const startEdit = (p: Preventivo) => {
    setEditingId(p.id);
    setEditValues({
      voce: p.voce,
      note: p.note || '',
      importoStimato: p.importoStimato ?? p.costoMed,
    });
  };

  const saveEdit = (p: Preventivo) => {
    const stimato = editValues.importoStimato;
    updatePreventivo.mutate(
      {
        id: p.id,
        projectId,
        data: {
          voce: editValues.voce,
          note: editValues.note || null,
          // Aggiorna lo stimato e mantiene allineati costoMin/Med/Max
          // per coerenza con le altre viste (LavoroCard usa costoMed).
          importoStimato: stimato,
          costoMin: stimato,
          costoMed: stimato,
          costoMax: stimato,
        },
      },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Eliminare questa voce?')) {
      deletePreventivo.mutate({ id, projectId });
    }
  };

  // Salva l'importo reale (consuntivo) di una voce.
  // La data di pagamento segue l'importo: si valorizza a oggi la prima volta che
  // il consuntivo viene inserito, e si azzera se il consuntivo viene cancellato.
  // Così il Resoconto sa non solo quanto, ma anche quando.
  const saveReale = (p: Preventivo, raw: string) => {
    const value = raw.trim();
    const current = p.importoReale ?? '';
    if (value === current) return;

    const svuotato = value === '';
    const dataPagamento = svuotato ? null : p.dataPagamento ?? new Date().toISOString().slice(0, 10);

    updatePreventivo.mutate({
      id: p.id,
      projectId,
      data: { importoReale: svuotato ? null : value, dataPagamento },
    });
  };

  // Toggle usato/nuovo su una voce a doppia stima
  const setTipo = (p: Preventivo, tipo: TipoAcquisto) => {
    if (p.tipoAcquisto === tipo) return;
    const price = tipo === 'usato' ? p.prezzoUsato : p.prezzoNuovo;
    const data: Record<string, string | null> = { tipoAcquisto: tipo };
    if (price != null && price !== '') {
      data.importoStimato = price;
      data.costoMin = price;
      data.costoMed = price;
      data.costoMax = price;
    }
    updatePreventivo.mutate({ id: p.id, projectId, data });
  };

  // Toggle globale: tutto usato / tutto nuovo
  const setAllTipo = (tipo: TipoAcquisto) => {
    for (const p of preventivi) {
      if (hasUsatoNuovo(p) && p.tipoAcquisto !== tipo) {
        setTipo(p, tipo);
      }
    }
  };

  // Attiva/disattiva una singola voce (esclusione dai totali)
  const toggleAttivo = (p: Preventivo) => {
    updatePreventivo.mutate({
      id: p.id,
      projectId,
      data: { attivo: !isAttiva(p) },
    });
  };

  // Attiva/disattiva tutte le voci di una categoria
  const setCategoriaAttivo = (items: Preventivo[], attivo: boolean) => {
    for (const p of items) {
      if (isAttiva(p) !== attivo) {
        updatePreventivo.mutate({ id: p.id, projectId, data: { attivo } });
      }
    }
  };

  // Attiva/disattiva tutte le voci del lavoro
  const setAllAttivo = (attivo: boolean) => {
    for (const p of preventivi) {
      if (isAttiva(p) !== attivo) {
        updatePreventivo.mutate({ id: p.id, projectId, data: { attivo } });
      }
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>CME - ${lavoro.nome}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #1a1a1a; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          h2 { font-size: 14px; color: #666; margin-top: 0; margin-bottom: 20px; font-weight: normal; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #f5f5f5; padding: 8px 12px; text-align: left; border-bottom: 2px solid #ddd; font-weight: 600; }
          th.num { text-align: right; }
          td { padding: 6px 12px; border-bottom: 1px solid #eee; }
          td.num { text-align: right; font-variant-numeric: tabular-nums; }
          tr.category td { background: #f9f9f9; font-weight: 600; padding-top: 12px; }
          tr.subtotal td { border-top: 1px solid #ccc; font-weight: 600; font-style: italic; }
          tr.total td { border-top: 2px solid #333; font-weight: 700; font-size: 14px; }
          .note { color: #888; font-size: 11px; }
          .footer { margin-top: 24px; font-size: 11px; color: #999; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Computo Metrico Estimativo</h1>
        <h2>${lavoro.nome}${lavoro.descrizione ? ' - ' + lavoro.descrizione : ''}</h2>
        ${content.innerHTML}
        <div class="footer">Generato il ${new Date().toLocaleDateString('it-IT')} - Progetto Casa</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleCopyText = () => {
    const lines: string[] = [];
    lines.push(`COMPUTO METRICO ESTIMATIVO - ${lavoro.nome}`);
    if (lavoro.descrizione) lines.push(lavoro.descrizione);
    lines.push('');
    lines.push(
      `${'Voce'.padEnd(45)} ${'Q.tà'.padStart(8)} ${'P.U.'.padStart(11)} ${'Stimato'.padStart(12)} ${'Reale'.padStart(12)} ${'Δ'.padStart(11)}`
    );
    lines.push('-'.repeat(102));

    for (const [categoria, items] of grouped) {
      lines.push(categoria);
      for (const p of items) {
        const qtaStr = p.quantita ? `${parseFloat(p.quantita)} ${p.unitaMisura ?? ''}`.trim() : '';
        const pu = p.prezzoUnitario ? formatCurrency(parseFloat(p.prezzoUnitario)) : '';
        const reale = p.importoReale ? formatCurrency(parseFloat(p.importoReale)) : '';
        const delta = getDelta(p);
        lines.push(
          `  ${p.voce.slice(0, 43).padEnd(43)} ${qtaStr.padStart(8)} ${pu.padStart(11)} ${formatCurrency(getImportoStimato(p)).padStart(12)} ${reale.padStart(12)} ${(delta ? formatCurrency(delta) : '').padStart(11)}`
        );
      }
      const cat = calculateCmeTotals(items);
      lines.push(
        `  ${'Subtotale'.padEnd(43)} ${''.padStart(8)} ${''.padStart(11)} ${formatCurrency(cat.stimato).padStart(12)} ${formatCurrency(cat.reale).padStart(12)} ${formatCurrency(cat.delta).padStart(11)}`
      );
      lines.push('');
    }

    lines.push('-'.repeat(102));
    lines.push(
      `  ${'TOTALE'.padEnd(43)} ${''.padStart(8)} ${''.padStart(11)} ${formatCurrency(totals.stimato).padStart(12)} ${formatCurrency(totals.reale).padStart(12)} ${formatCurrency(totals.delta).padStart(11)}`
    );

    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Computo Metrico Estimativo</h2>
            <p className="text-sm text-gray-500">
              {lavoro.nome}
              {lavoro.descrizione ? ` - ${lavoro.descrizione}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Global usato/nuovo toggle */}
            {hasAnyDouble && (
              <div className="flex items-center gap-1 mr-1">
                <span className="text-xs text-gray-400">Tutto:</span>
                <button
                  onClick={() => setAllTipo('usato')}
                  className="btn-secondary text-xs py-1.5 px-2.5"
                  title="Imposta tutte le voci a doppia stima su USATO"
                >
                  Usato
                </button>
                <button
                  onClick={() => setAllTipo('nuovo')}
                  className="btn-secondary text-xs py-1.5 px-2.5"
                  title="Imposta tutte le voci a doppia stima su NUOVO"
                >
                  Nuovo
                </button>
              </div>
            )}
            {/* Global attiva/disattiva toggle */}
            {preventivi.length > 0 && (
              <div className="flex items-center gap-1 mr-1">
                <span className="text-xs text-gray-400">Voci:</span>
                <button
                  onClick={() => setAllAttivo(true)}
                  disabled={allAttive}
                  className="btn-secondary text-xs py-1.5 px-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Riattiva tutte le voci"
                >
                  Attiva tutto
                </button>
                <button
                  onClick={() => setAllAttivo(false)}
                  disabled={noneAttive}
                  className="btn-secondary text-xs py-1.5 px-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Disattiva tutte le voci"
                >
                  Disattiva tutto
                </button>
              </div>
            )}
            {/* Copy */}
            <button onClick={handleCopyText} className="btn-secondary text-xs py-1.5 px-3" title="Copia come testo">
              {copied ? (
                <>
                  <svg className="w-3.5 h-3.5 mr-1.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copiato
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copia
                </>
              )}
            </button>
            {/* Print/PDF */}
            <button onClick={handlePrint} className="btn-secondary text-xs py-1.5 px-3" title="Stampa / Salva PDF">
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Stampa / PDF
            </button>
            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1 p-5">
          {preventivi.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500">
              Nessuna voce nel computo. Usa Stima AI o Carica Preventivo per generare il CME.
            </div>
          ) : (
            <div ref={printRef}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Voce</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 w-24 num">Q.tà</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 w-24 num">P.U.</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 w-28 num">Stimato</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 w-28 num">Reale</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 w-24 num">Δ</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(([categoria, items]) => (
                    <CmeCategory
                      key={categoria}
                      categoria={categoria}
                      items={items}
                      editingId={editingId}
                      editValues={editValues}
                      setEditValues={setEditValues}
                      onStartEdit={startEdit}
                      onSaveEdit={saveEdit}
                      onCancelEdit={() => setEditingId(null)}
                      onDelete={handleDelete}
                      onSaveReale={saveReale}
                      onSetTipo={setTipo}
                      onToggleAttivo={toggleAttivo}
                      onSetCategoriaAttivo={setCategoriaAttivo}
                      isSaving={updatePreventivo.isPending}
                    />
                  ))}
                  {/* Grand total */}
                  <tr className="border-t-2 border-gray-800">
                    <td className="py-3 px-3 font-bold text-gray-900">TOTALE</td>
                    <td></td>
                    <td></td>
                    <td className="py-3 px-3 text-right font-bold text-gray-900 tabular-nums">{formatCurrency(totals.stimato)}</td>
                    <td className="py-3 px-3 text-right font-bold text-primary-700 tabular-nums">{formatCurrency(totals.reale)}</td>
                    <td className={`py-3 px-3 text-right font-bold tabular-nums ${deltaClass(totals.delta)}`}>
                      {totals.delta === 0 ? '—' : formatCurrency(totals.delta)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer summary */}
        {preventivi.length > 0 && (
          <div className="border-t border-gray-200 px-5 py-3 bg-gray-50 rounded-b-xl shrink-0 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {preventivi.length} voc{preventivi.length === 1 ? 'e' : 'i'} in {grouped.length} categori
              {grouped.length === 1 ? 'a' : 'e'}
              {escluse.count > 0 && (
                <span className="ml-2 text-amber-600">
                  · {escluse.count} disattivat{escluse.count === 1 ? 'a' : 'e'} ({formatCurrency(escluse.stimato)} esclus{escluse.count === 1 ? 'o' : 'i'})
                </span>
              )}
            </span>
            <span className="text-sm text-gray-600">
              {escluse.count > 0 && <span className="mr-1 text-gray-400">Attivo</span>}
              Stimato <strong className="text-gray-900 tabular-nums">{formatCurrency(totals.stimato)}</strong>
              <span className="mx-2 text-gray-300">·</span>
              Reale <strong className="text-primary-700 tabular-nums">{formatCurrency(totals.reale)}</strong>
              <span className="mx-2 text-gray-300">·</span>
              Δ <strong className={`tabular-nums ${deltaClass(totals.delta)}`}>{totals.delta === 0 ? '—' : formatCurrency(totals.delta)}</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Δ color: rosso se sforamento (>0), verde se risparmio (<0)
function deltaClass(delta: number): string {
  if (delta > 0) return 'text-danger-600';
  if (delta < 0) return 'text-green-600';
  return 'text-gray-400';
}

// Inline-editable "Reale" cell with local state
function RealeCell({
  preventivo,
  onSave,
}: {
  preventivo: Preventivo;
  onSave: (p: Preventivo, value: string) => void;
}) {
  const [value, setValue] = useState(preventivo.importoReale ?? '');

  return (
    <input
      type="number"
      inputMode="decimal"
      step="0.01"
      min="0"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onSave(preventivo, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder="—"
      className="w-full text-sm py-1 px-2 border border-gray-200 rounded text-right tabular-nums focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
    />
  );
}

// Sub-component for a category group
function CmeCategory({
  categoria,
  items,
  editingId,
  editValues,
  setEditValues,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onSaveReale,
  onSetTipo,
  onToggleAttivo,
  onSetCategoriaAttivo,
  isSaving,
}: {
  categoria: string;
  items: Preventivo[];
  editingId: string | null;
  editValues: { voce: string; note: string; importoStimato: string };
  setEditValues: React.Dispatch<React.SetStateAction<{ voce: string; note: string; importoStimato: string }>>;
  onStartEdit: (p: Preventivo) => void;
  onSaveEdit: (p: Preventivo) => void;
  onCancelEdit: () => void;
  onDelete: (id: string) => void;
  onSaveReale: (p: Preventivo, value: string) => void;
  onSetTipo: (p: Preventivo, tipo: TipoAcquisto) => void;
  onToggleAttivo: (p: Preventivo) => void;
  onSetCategoriaAttivo: (items: Preventivo[], attivo: boolean) => void;
  isSaving: boolean;
}) {
  const catTotals = calculateCmeTotals(items);
  const catAllAttive = items.every(isAttiva);

  return (
    <>
      {/* Category header */}
      <tr className="category">
        <td colSpan={7} className="py-2 px-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-700 text-xs uppercase tracking-wider">{categoria}</span>
            <button
              onClick={() => onSetCategoriaAttivo(items, !catAllAttive)}
              className="text-xs text-gray-400 hover:text-primary-600 normal-case tracking-normal font-normal print:hidden"
              title={catAllAttive ? 'Disattiva tutte le voci della categoria' : 'Riattiva tutte le voci della categoria'}
            >
              {catAllAttive ? 'Disattiva categoria' : 'Attiva categoria'}
            </button>
          </div>
        </td>
      </tr>
      {/* Items */}
      {items.map((p) => {
        const delta = getDelta(p);
        const qtaNum = p.quantita ? parseFloat(p.quantita) : null;
        const attiva = isAttiva(p);
        return (
          <tr
            key={p.id}
            className={`border-b border-gray-100 hover:bg-gray-50 group align-top ${
              attiva ? '' : 'opacity-50'
            }`}
          >
            {editingId === p.id ? (
              <>
                <td className="py-2 px-3" colSpan={3}>
                  <input
                    type="text"
                    value={editValues.voce}
                    onChange={(e) => setEditValues((v) => ({ ...v, voce: e.target.value }))}
                    className="w-full text-sm py-1 px-2 border rounded"
                  />
                  <input
                    type="text"
                    value={editValues.note}
                    onChange={(e) => setEditValues((v) => ({ ...v, note: e.target.value }))}
                    className="w-full text-xs py-1 px-2 border rounded mt-1 text-gray-500"
                    placeholder="Note..."
                  />
                </td>
                <td className="py-2 px-3">
                  <input
                    type="number"
                    value={editValues.importoStimato}
                    onChange={(e) => setEditValues((v) => ({ ...v, importoStimato: e.target.value }))}
                    className="w-full text-sm py-1 px-2 border rounded text-right"
                    step="0.01"
                    min="0"
                  />
                </td>
                <td colSpan={2}></td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => onSaveEdit(p)} disabled={isSaving} className="p-1 text-primary-600 hover:bg-primary-50 rounded" title="Salva">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </button>
                    <button onClick={onCancelEdit} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Annulla">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </td>
              </>
            ) : (
              <>
                <td className="py-2 px-3">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={attiva}
                      onChange={() => onToggleAttivo(p)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-400 cursor-pointer shrink-0 print:hidden"
                      title={attiva ? 'Disattiva voce (escludi dai totali)' : 'Riattiva voce'}
                    />
                    <div>
                      <span className={attiva ? 'text-gray-900' : 'text-gray-500 line-through'}>{p.voce}</span>
                      {!attiva && (
                        <span className="ml-2 align-middle text-[10px] font-medium uppercase tracking-wide text-amber-600 no-underline">
                          esclusa
                        </span>
                      )}
                      {p.note && <span className="note block text-xs text-gray-400 mt-0.5">{p.note}</span>}
                    </div>
                  </div>
                  {hasUsatoNuovo(p) && (
                    <div className="inline-flex items-center mt-1 rounded-md border border-gray-200 overflow-hidden text-xs">
                      <button
                        onClick={() => onSetTipo(p, 'usato')}
                        className={`px-2 py-0.5 transition-colors ${
                          p.tipoAcquisto === 'usato'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                        title={p.prezzoUsato ? `Usato: ${formatCurrency(parseFloat(p.prezzoUsato))}` : 'Usato'}
                      >
                        Usato
                      </button>
                      <button
                        onClick={() => onSetTipo(p, 'nuovo')}
                        className={`px-2 py-0.5 transition-colors border-l border-gray-200 ${
                          p.tipoAcquisto === 'nuovo'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                        title={p.prezzoNuovo ? `Nuovo: ${formatCurrency(parseFloat(p.prezzoNuovo))}` : 'Nuovo'}
                      >
                        Nuovo
                      </button>
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-gray-600 num">
                  {qtaNum !== null ? (
                    <span>
                      {qtaNum}
                      {p.unitaMisura ? <span className="text-gray-400 text-xs ml-1">{p.unitaMisura}</span> : null}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-gray-600 num">
                  {p.prezzoUnitario ? formatCurrency(parseFloat(p.prezzoUnitario)) : <span className="text-gray-300">—</span>}
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-gray-900 font-medium num">
                  {formatCurrency(getImportoStimato(p))}
                </td>
                <td className="py-2 px-2">
                  <RealeCell preventivo={p} onSave={onSaveReale} />
                </td>
                <td className={`py-2 px-3 text-right tabular-nums num ${deltaClass(delta)}`}>
                  {delta === 0 ? <span className="text-gray-300">—</span> : formatCurrency(delta)}
                </td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onStartEdit(p)} className="p-1 text-gray-400 hover:text-primary-600 rounded" title="Modifica">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => onDelete(p.id)} className="p-1 text-gray-400 hover:text-danger-600 rounded" title="Elimina">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </td>
              </>
            )}
          </tr>
        );
      })}
      {/* Category subtotal */}
      <tr className="subtotal">
        <td className="py-1.5 px-3 text-xs text-gray-500 italic">Subtotale {categoria}</td>
        <td></td>
        <td></td>
        <td className="py-1.5 px-3 text-right text-xs text-gray-600 italic font-medium tabular-nums num">{formatCurrency(catTotals.stimato)}</td>
        <td className="py-1.5 px-3 text-right text-xs text-gray-600 italic tabular-nums num">{formatCurrency(catTotals.reale)}</td>
        <td className={`py-1.5 px-3 text-right text-xs italic tabular-nums num ${deltaClass(catTotals.delta)}`}>
          {catTotals.delta === 0 ? '' : formatCurrency(catTotals.delta)}
        </td>
        <td></td>
      </tr>
    </>
  );
}
